import { commonHeader, endpoints, generateEndpoint } from "~/config/apiConfig";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type { RegisterBody } from "~/interface/auth";
import {
  type AuthenticatedUserInfo,
  isNotAuthenticatedUserInfo,
  type NotAuthenticatedUserInfo,
  type UserInfo,
} from "~/interface/userInfo";

// signinレスポンスの型定義
export type SigninResponse =
  | { type: "authenticated"; userInfo: AuthenticatedUserInfo }
  | { type: "needs_signup" }
  | { type: "needs_signup_invitation"; userInfo: NotAuthenticatedUserInfo }
  | { type: "error" };

export class AuthApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  private getRequestBody(baseBody: object, extraBody?: object): object {
    // ベースとなる共通データと、メソッドごとの追加データをマージ
    return { ...baseBody, ...extraBody };
  }

  async signin(): Promise<SigninResponse> {
    // ad_id is obtained from authentication token by backend middleware
    const signin = generateEndpoint(`/signin/users/me`);
    const requestInfo = this.getRequestInfo("GET");
    const requestBody = this.getRequestBody(requestInfo);

    try {
      const response = await fetch(signin, requestBody);
      if (response.ok) {
        const userInfo: UserInfo = await response.json();

        // ad_idの有無で招待ユーザーか認証済みユーザーかを判定
        if (isNotAuthenticatedUserInfo(userInfo)) {
          return { type: "needs_signup_invitation", userInfo };
        }
        return { type: "authenticated", userInfo };
      } else if (response.status === 404) {
        return { type: "needs_signup" };
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return { type: "error" };
      }
    } catch (error) {
      console.error(error);
      return { type: "error" };
    }
  }

  async signup(requestBody: RegisterBody): Promise<Response | null> {
    const signup = generateEndpoint("/signup/register");

    try {
      const requestInfo = this.getRequestInfo("POST", requestBody);
      const response = await fetch(signup, requestInfo);

      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async signout(): Promise<Response | null> {
    const signout = endpoints.logoutApp;

    try {
      const requestInfo = this.getRequestInfo("GET");
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(signout, requestBody);

      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async activate(): Promise<UserInfo | null> {
    // 招待ユーザーのad_idを紐づけて有効化する
    const activate = generateEndpoint("/signup/activate");
    const requestInfo = this.getRequestInfo("POST");

    try {
      const response = await fetch(activate, requestInfo);

      if (response.ok) {
        const userInfo: UserInfo = await response.json();
        return userInfo;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
